import type {
  BlockType,
  CombinatorKey,
} from '@packages/systems/core/utils/StyleTypes';
import type {
  FileRecordProps,
  FileRecord,
} from '@packages/systems/core/records/FileRecord';
import type {Interaction, Timeline} from '@packages/systems/ix3/core';
import type {Component} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {IX2StoreStateType} from '@packages/systems/ix2/types-core';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import type {JSONNode} from '@packages/shared/types/JSONNode';

/** Identifies a copied component so the destination can match its installed library. */
export type XscpLibraryComponent = {
  // Local component ID on the source site, shared by its copied instances.
  id: string;
  // Library version/plugin ID followed by the component ID within that library.
  origin: [string, string];
  // Stable library identity across versions.
  profileId: string;
};

/**
 * Offers linked library instances alongside expanded alternatives for destinations
 * that cannot retain them. An alternative can still retain nested library instances.
 */
export type XscpLibraryCopyTree = {
  data: XscpData;
  // Keyed by instance node ID, since each instance has its own overrides and slots.
  expandedInstanceIds: Record<string, string>;
  // Shared expansions are serialized once; each instance references a variant ID.
  expandedInstances: Record<string, XscpLibraryCopyVariant>;
};

export type XscpLibraryCopyVariant = Pick<
  XscpLibraryCopyTree,
  'data' | 'expandedInstanceIds'
>;

/**
 * Stages destination library changes until the paste is accepted. Resource fields
 * contain only the changes to commit; this object is never serialized to the clipboard.
 */
export type XscpLibraryImport = {
  // Preserve the input before validation splits a tab into its link and pane.
  element: ExpressionElement;
  // Retained references to recheck if asset loading delays the paste.
  references: XscpLibraryComponent[];
  // Source component ID -> reused or newly prepared destination component ID.
  componentIds: Record<string, string>;
  components: Component.ComponentMap;
  styles: Pick<
    StyleBlockStateType,
    | 'styleBlocks'
    | 'parentIndex'
    | 'globalOrder'
    | 'styleBlockIds'
    | 'cssPropertyMap'
  >;
  ix2: Pick<IX2StoreStateType, 'events' | 'actionLists' | 'interactions'>;
  ix3: {interactions: Interaction[]; timelines: Timeline[]};
  assetsToImport: Record<string, FileRecord>;
  // Capture the paste context so asset loading cannot redirect it to another page.
  pageId: string;
  // Preserve the selection and component-editing context across the asset wait.
  selection: Pick<
    UiNodeStateType,
    | 'selectedNodeNativeId'
    | 'selectedNativeIdPath'
    | 'focusedSymbolNativeIdPath'
  >;
};

export type XscpLog = {
  sourceSiteId?: string;
  unlinkedSymbolCount: number;
  droppedLinks: number;
  dynBindRemovedCount: number;
  dynListBindRemovedCount: number;
  paginationRemovedCount: number;
  universalBindingsRemovedCount: number;
  codeComponentsRemovedCount: number;
  richTextComponentsStripped: boolean;
};

export type XscpData = {
  type: '@webflow/XscpData';
  payload: {
    nodes: Array<JSONNode>;
    styles: Array<{
      _id: string;
      fake: boolean;
      type: BlockType;
      name: string;
      comb: CombinatorKey;
      styleLess: string;
      variants: {
        [key: string]: {
          styleLess: string;
        };
      };
      children: Array<string>;
    }>;
    assets: Array<FileRecordProps>;
    ix1: Array<{
      _id: string;
      slug: string;
      name: string;
      value: Record<any, any>;
    }>;
    ix2: {
      interactions: Array<Record<any, any>>;
      events: Array<Record<any, any>>;
      actionLists: Array<Record<any, any>>;
    };
    ix3?: {
      interactions: Array<Interaction>;
      timelines: Array<Timeline>;
    };
    expandUserComponents?: boolean;
    // Optional linked alternative to the outer payload's fully expanded content.
    libraryRetention?: XscpLibraryCopyTree;
    // Identifies the library components still referenced by this payload's nodes.
    libraryComponents?: XscpLibraryComponent[];
  };
  meta: XscpLog;
};

export const isXscpData = (data: unknown): data is XscpData => {
  if (data === null || typeof data !== 'object') return false;
  return 'type' in data && data.type === '@webflow/XscpData';
};
